import { fabric } from "fabric";
import type { IImageOptions, Image } from "fabric/fabric-impl";
import type { Object } from "../types/canvasTypes";
import { BASE_ORIGIN_OPTIONS, DEFAULT_IMAGE_OPTIONS } from "../constants/canvasConstants";
import {
  calculateObjectHeightConsideringScale,
  calculateObjectWidthConsideringScale,
  getProcessedCoordinatesFromStoreForCenteredObject,
} from "../utils/canvasUtils";
import type { FabricImage } from "../types/imageTypes";
import type { ViewportShape } from "../types/objectTypes";

export function createImage(options?: IImageOptions) {
  return new fabric.Image("", {
    ...DEFAULT_IMAGE_OPTIONS,
    ...BASE_ORIGIN_OPTIONS,
    ...options,
  });
}

export function calculateDefaultOptionsForImage(
  image: Pick<FabricImage, "width" | "height">,
  viewPort: Pick<ViewportShape, "width" | "height">,
) {
  const { width: viewPortWidth, height: viewPortHeight } = viewPort;
  const { width: imageWidth, height: imageHeight } = image;

  if (
    imageWidth === undefined ||
    imageHeight === undefined ||
    viewPortWidth === undefined ||
    viewPortHeight === undefined
  ) {
    return {};
  }

  const scale = Math.min(1, viewPortWidth / imageWidth, viewPortHeight / imageHeight);

  const scaleOptions = { scaleX: scale, scaleY: scale };
  const position = calculateCenteredPositionForImage({ ...image, ...scaleOptions }, viewPort);

  // Set scale, position and default options
  return {
    ...DEFAULT_IMAGE_OPTIONS,
    ...BASE_ORIGIN_OPTIONS,
    ...position,
    ...scaleOptions,
  };
}

export const isImage = (object: Object): object is Image => object.type === "image";

function calculateCenteredPositionForImage(
  image: Pick<FabricImage, "width" | "height" | "scaleX" | "scaleY">,
  viewPort: Pick<ViewportShape, "width" | "height">,
) {
  const scaledWidth = calculateObjectWidthConsideringScale(image);
  const scaledHeight = calculateObjectHeightConsideringScale(image);
  const { width: viewPortWidth = 0, height: viewPortHeight = 0 } = viewPort;

  return getProcessedCoordinatesFromStoreForCenteredObject({
    top: viewPortHeight / 2 - scaledHeight / 2,
    left: viewPortWidth / 2 - scaledWidth / 2,
    width: scaledWidth,
    height: scaledHeight,
  });
}
